import React, { useState } from "react";
import { Button } from "@web-monorepo/mds";
import { logSparksEvent } from "@src/utils/sparks/logSparksEvent";
import type { SparksCancellationWinbackResolution } from "./SparksCancellationWinbackResolver";

type PlanId = "annual" | "monthly";

export type SparksCancellationWinbackPlan = {
  href: string;
  price: string;
  compactPrice: string;
  cadence: "month" | "year";
  previousPrice?: string;
  desktopSubtext?: string;
  badge?: string;
};

export type SparksCancellationWinbackPlans = {
  annual: SparksCancellationWinbackPlan;
  monthly: SparksCancellationWinbackPlan;
};

type Props = {
  resolution: SparksCancellationWinbackResolution;
  plans: SparksCancellationWinbackPlans;
};

const PLAN_SELECTED_ICON = "/images/sparks/paywall/plan-selected.svg";
const PLAN_UNSELECTED_ICON = "/images/sparks/paywall/plan-unselected.svg";

const getTrackingMetadata = (resolution: SparksCancellationWinbackResolution, plan: PlanId) => ({
  platform: "web",
  plan,
  promotion: resolution === "eligible" ? "free_month" : "free_week",
  resolution,
  has_winback_id: true,
});

const logPlanChanged = (resolution: SparksCancellationWinbackResolution, plan: PlanId) => {
  void logSparksEvent({
    eventName: "parent.sparks.winback.paywall.plan_changed",
    metadata: getTrackingMetadata(resolution, plan),
  });
};

const logCtaTapped = (resolution: SparksCancellationWinbackResolution, plan: PlanId) => {
  void logSparksEvent({
    eventName: "parent.sparks.winback.paywall.cta.tapped",
    metadata: getTrackingMetadata(resolution, plan),
  });
};

const presentationByResolution = {
  eligible: {
    title: "Claim your free month!",
    subtitle: "Dojo Sparks gives your child the power of 1-on-1 reading coaching at home — all for less than $1 a day.",
    cta: "Start my free month",
    trialBenefit: "30 days free",
    trialDescription: "30 day free trial",
  },
  expired: {
    title: "Your promotion has expired.",
    subtitle: "Unlock the full experience and a 7 day free trial below.",
    cta: "Start my free week",
    trialBenefit: "7 day free trial",
    trialDescription: "7 day free trial",
  },
  redeemed: {
    title: "This promotion has already been used",
    subtitle: "Unlock the full experience and a 7 day free trial below.",
    cta: "Start my free week",
    trialBenefit: "7 day free trial",
    trialDescription: "7 day free trial",
  },
  invalid: {
    title: "Your promotion is not valid",
    subtitle: "Unlock the full experience and a 7 day free trial below.",
    cta: "Start my free week",
    trialBenefit: "7 day free trial",
    trialDescription: "7 day free trial",
  },
} as const satisfies Record<
  SparksCancellationWinbackResolution,
  {
    title: string;
    subtitle: string;
    cta: string;
    trialBenefit: string;
    trialDescription: string;
  }
>;

const PlanStatusIcon = ({ selected }: { selected: boolean }) => (
  <img
    src={selected ? PLAN_SELECTED_ICON : PLAN_UNSELECTED_ICON}
    alt=""
    aria-hidden="true"
    className="h-20 w-20 shrink-0"
  />
);

const MobilePlanOption = ({
  planId,
  plan,
  selected,
  onSelect,
}: {
  planId: PlanId;
  plan: SparksCancellationWinbackPlan;
  selected: boolean;
  onSelect: () => void;
}) => {
  const tone = selected ? "text-brand-secondary" : "text-neutral-500";
  const label = planId === "annual" ? "Annual" : "Monthly";

  return (
    <div className="flex w-full flex-col items-start">
      {plan.badge && (
        <span className="ml-[19px] flex h-24 items-center justify-center rounded-t-2xs bg-brand-secondary px-sm text-[12px] font-bold uppercase tracking-[0.7px] text-[#fffae6]">
          {plan.badge}
        </span>
      )}
      <label
        className={`flex min-h-[66px] w-full cursor-pointer items-center justify-between rounded-lg border-[2.5px] bg-[#fffae6] px-[24px] py-sm transition-colors focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-primary ${
          selected ? "border-brand-secondary" : "border-neutral-500"
        }`}
      >
        <input
          type="radio"
          name="sparks-cancellation-winback-plan"
          value={planId}
          checked={selected}
          onChange={onSelect}
          className="sr-only"
        />
        <span className="flex min-w-0 flex-col items-start justify-center">
          <span className={`font-ginto text-[18px] font-bold leading-[1.3] ${tone}`}>{label}</span>
          {plan.previousPrice && (
            <span className="flex items-center gap-x-2xs font-ginto text-[16px] leading-[1.4]">
              <span className="text-neutral-500 line-through">{plan.previousPrice}</span>
              <span className="text-brand-secondary">{plan.price}</span>
            </span>
          )}
        </span>
        <span className="ml-sm flex shrink-0 items-center gap-x-sm">
          <span className={`font-ginto text-[16px] font-bold leading-[1.4] ${tone}`}>{plan.compactPrice}</span>
          <PlanStatusIcon selected={selected} />
        </span>
      </label>
    </div>
  );
};

const MobilePricing = ({
  plans,
  cta,
  trialDescription,
  resolution,
}: {
  plans: SparksCancellationWinbackPlans;
  cta: string;
  trialDescription: string;
  resolution: SparksCancellationWinbackResolution;
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>("annual");
  const selectedPlan = plans[selectedPlanId];

  return (
    <div className="mt-[49px] flex w-full max-w-[321px] flex-col lg:hidden">
      <fieldset className="flex w-full flex-col gap-y-[12px]">
        <legend className="sr-only">Choose a Dojo Sparks plan</legend>
        <MobilePlanOption
          planId="annual"
          plan={plans.annual}
          selected={selectedPlanId === "annual"}
          onSelect={() => {
            setSelectedPlanId("annual");
            logPlanChanged(resolution, "annual");
          }}
        />
        <MobilePlanOption
          planId="monthly"
          plan={plans.monthly}
          selected={selectedPlanId === "monthly"}
          onSelect={() => {
            setSelectedPlanId("monthly");
            logPlanChanged(resolution, "monthly");
          }}
        />
      </fieldset>

      <Button
        as="a"
        href={selectedPlan.href}
        variant="primary"
        size="l"
        className="mt-[24px] w-full"
        data-testid="winback-mobile-pricing-cta"
        onClick={() => logCtaTapped(resolution, selectedPlanId)}
      >
        {cta}
      </Button>
      <p className="mt-[15px] text-center font-ginto text-[16px] leading-[1.4] text-brand-secondary">
        {trialDescription}, then just {selectedPlan.price} per {selectedPlan.cadence}
        <br />
        Cancel anytime in App Store
      </p>
    </div>
  );
};

const DesktopPlanCard = ({
  planId,
  plan,
  cta,
  resolution,
}: {
  planId: PlanId;
  plan: SparksCancellationWinbackPlan;
  cta: string;
  resolution: SparksCancellationWinbackResolution;
}) => {
  const isAnnual = planId === "annual";

  return (
    <article
      className={
        isAnnual
          ? "relative z-10 -ml-[21px] flex w-[471px] flex-col items-start gap-y-[45px] overflow-hidden rounded-md border-[3px] border-brand-secondary bg-[#fffae6] px-[38px] py-[32px]"
          : "relative z-0 mt-[19px] flex w-[392px] flex-col items-start gap-y-[37px] rounded-md border-[3px] border-brand-secondary bg-[#fffae6] px-[32px] py-[27px]"
      }
    >
      {plan.badge && (
        <span className="absolute right-[14px] top-[20px] flex h-[27px] items-center rounded-3xs bg-brand-secondary px-[7px] font-ginto text-[13px] font-bold uppercase leading-[1.2] text-white">
          {plan.badge}
        </span>
      )}
      <div className={`flex w-full flex-col items-start ${isAnnual ? "gap-y-[15px]" : "gap-y-[13px]"}`}>
        <h2 className="font-ginto text-[28px] font-bold leading-[1.3] text-brand-primary">
          {isAnnual ? "Annual" : "Monthly"}
        </h2>
        <p className="font-melun text-[55px] font-bold leading-none text-[#171a1e]">{plan.price}</p>
        {plan.desktopSubtext ? (
          <p className="font-ginto text-[19px] leading-[1.3] text-[#5d5c62]">{plan.desktopSubtext}</p>
        ) : (
          <span className="invisible font-ginto text-[19px] leading-[1.3]" aria-hidden="true">
            spacer
          </span>
        )}
      </div>
      <Button
        as="a"
        href={plan.href}
        variant="primary"
        size="m"
        data-testid={`winback-desktop-${planId}-cta`}
        onClick={() => logCtaTapped(resolution, planId)}
      >
        {cta}
      </Button>
    </article>
  );
};

const DesktopPricing = ({
  plans,
  cta,
  trialBenefit,
  resolution,
}: {
  plans: SparksCancellationWinbackPlans;
  cta: string;
  trialBenefit: string;
  resolution: SparksCancellationWinbackResolution;
}) => (
  <div className="mt-[70px] hidden flex-col items-center lg:flex">
    <div className="flex items-center justify-center">
      <DesktopPlanCard planId="monthly" plan={plans.monthly} cta={cta} resolution={resolution} />
      <DesktopPlanCard planId="annual" plan={plans.annual} cta={cta} resolution={resolution} />
    </div>

    <div className="mt-[41px] flex w-[333px] flex-col items-center gap-y-[10px] text-brand-secondary">
      <p className="font-melun text-[24px] font-medium leading-[1.2]">Select from one of our plans</p>
      <div className="flex items-center gap-x-lg font-ginto text-[16px] leading-[1.4]">
        <span className="flex items-center gap-x-[6px] whitespace-nowrap">
          <img src="/images/sparks/paywall/free-trial.svg" alt="" aria-hidden="true" className="h-24 w-24" />
          {trialBenefit}
        </span>
        <span className="flex items-center gap-x-[6px] whitespace-nowrap">
          <img src="/images/sparks/paywall/cancel-anytime.svg" alt="" aria-hidden="true" className="h-24 w-24" />
          Cancel anytime
        </span>
      </div>
    </div>
  </div>
);

export const SparksCancellationWinbackPaywall = ({ resolution, plans }: Props) => {
  const presentation = presentationByResolution[resolution];

  return (
    <main className="brand-sparks min-h-[100svh] w-full bg-sky-300 text-brand-secondary">
      <div className="mx-auto flex min-h-[100svh] w-full max-w-[1440px] flex-col items-center px-[20px] pb-[64px] pt-[55px] lg:px-[32px] lg:pb-[96px] lg:pt-[51px]">
        <header className="flex w-full max-w-[1060px] flex-col items-center gap-y-[12px] text-center lg:gap-y-[13px]">
          <h1 className="max-w-[336px] font-melun text-[36px] font-bold leading-[1.2] lg:max-w-none lg:whitespace-nowrap lg:text-[70px]">
            {presentation.title}
          </h1>
          <p className="max-w-[336px] font-ginto text-[18px] leading-[1.3] lg:max-w-[1050px] lg:text-[21px]">
            {presentation.subtitle}
          </p>
        </header>

        <MobilePricing
          plans={plans}
          cta={presentation.cta}
          trialDescription={presentation.trialDescription}
          resolution={resolution}
        />
        <DesktopPricing
          plans={plans}
          cta={presentation.cta}
          trialBenefit={presentation.trialBenefit}
          resolution={resolution}
        />
      </div>
    </main>
  );
};
